import type { ReactNode } from "react";
import { cn } from "./cn";

export const CARD_SURFACES = {
  card: "bg-card",
  surface: "bg-surface",
  app: "bg-app",
} as const satisfies Record<string, string>;

export type CardSurface = keyof typeof CARD_SURFACES;

export const CARD_PADDINGS = {
  none: "",
  sm: "p-3",
  md: "p-4",
  lg: "p-6",
} as const satisfies Record<string, string>;

export type CardPadding = keyof typeof CARD_PADDINGS;

export interface CardProps {
  children: ReactNode;
  surface?: CardSurface;
  padding?: CardPadding;
  bordered?: boolean;
  hoverable?: boolean;
  className?: string;
  onClick?: () => void;
}

export function Card({
  children,
  surface = "card",
  padding = "md",
  bordered = true,
  hoverable = false,
  className,
  onClick,
}: CardProps) {
  return (
    <div
      onClick={onClick}
      className={cn(
        "rounded-2xl",
        CARD_SURFACES[surface],
        CARD_PADDINGS[padding],
        bordered ? "border border-line" : undefined,
        hoverable ? "cursor-pointer transition-colors hover:bg-subtle" : undefined,
        className,
      )}
    >
      {children}
    </div>
  );
}

export interface CardHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  className?: string;
}

export function CardHeader({ title, subtitle, actions, className }: CardHeaderProps) {
  return (
    <div className={cn("flex items-start justify-between gap-3", className)}>
      <div className="min-w-0">
        <h3 className="truncate text-sm font-semibold text-content">{title}</h3>
        {subtitle ? <p className="mt-0.5 text-xs text-content-secondary">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}
